import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import useEntityStore from "../../stores/entityStore";

const log = createFileLogger('FOLDER_CREATE');

export const folderCreateCommand: SelfContainedCommand = {
  id: 'folder_create',
  description: `Create a folder (group) to organize entities.

**SUPPORTED ENTITIES:**
All entity types

**EXAMPLE:**

{ "name": "Research Papers 2026" }`,
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Folder name. Be descriptive and specific.',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Folder name cannot be empty';
          if (value.length > 100) return 'Folder name too long (max 100 characters)';
          return null;
        },
        examples: [
          'Research Papers',
          'Q4 2024 Reports',
          'Client Projects',
          'Meeting Notes'
        ]
      },
      {
        name: 'parentFolderId',
        type: 'string',
        description: 'Optional parent folder ID (group_*) to create nested folder inside',
        required: false,
        examples: [
          'group_guitar_ocean_zodiac',
          'group_meadow_silver_compass',
          'group_cascade_thunder_phoenix'
        ]
      },
      {
        name: 'color',
        type: 'string',
        description: 'Optional color for the folder (hex code or color name)',
        required: false,
        examples: ['#3b82f6', '#10b981', '#f59e0b', 'blue', 'green']
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { name: 'Research Papers', color: '#3b82f6' },
        expectedOutput: createSuccessResult('Folder created successfully', {
          folder: {
            id: 'group_123',
            name: 'Research Papers',
            entityType: 'Group'
          }
        }),
        description: 'Create a top-level folder with a color',
        context: 'When organizing documents by category'
      },
      {
        input: { name: 'Nature Submissions', parentFolderId: 'group_123' },
        expectedOutput: createSuccessResult('Folder created successfully', {
          folder: {
            id: 'group_456',
            name: 'Nature Submissions',
            entityType: 'Group',
            parentId: 'group_123'
          }
        }),
        description: 'Create a nested folder inside another folder',
        context: 'When creating hierarchical organization'
      }
    ],
    bad: [
      {
        input: { name: '' },
        error: 'Folder name cannot be empty',
        why: 'Empty names are not allowed',
        fix: 'Provide a descriptive folder name'
      }
    ]
  },
  
  performance: {
    expectedDuration: 300,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { name, parentFolderId, color, spaceId: providedSpaceId, edge_pos } = params;
    const spaceId = providedSpaceId || context.spaceId;
    
    try {
      log.info('[folder_create] Creating folder:', { name, parentFolderId, color, spaceId, edge_pos });
      
      const entityStore = useEntityStore.getState();
      const folder = await entityStore.actions.createFolder(name, parentFolderId, {
        color,
        spaceId,
        ...(edge_pos ? { edge_pos } : {})
      });
      
      log.info('[folder_create] ✅ Folder created:', { id: folder._id, name: folder.name });
      
      return createSuccessResult(
        `Folder "${name.trim()}" created successfully${parentFolderId ? ' (nested)' : ''}`,
        {
          folder: {
            id: folder._id || folder.id,
            name: folder.name,
            entityType: folder.entityType,
            color: (folder as any).color,
            parentId: parentFolderId || null,
            created_at: folder.created_at
          }
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityStore.actions',
            affectedEntities: [folder._id, ...(parentFolderId ? [parentFolderId] : [])]
          } as any,
          ui: {
            refreshContext: true,
            resultPreview: 'summary'
          }
        }
      );
      
    } catch (error: any) {
      log.error('[folder_create] Error:', error);
      return createErrorResult(
        'Folder creation failed',
        `Failed to create folder: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { name, parentFolderId, spaceId },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

